// src/features/simulators/mobile-kiosk/hooks/useMobileKioskSimulation.ts

import { useCallback } from 'react';
import type { AnalyticsClient } from '../../shared/utils/analyticsClient';
import type { LogEntry } from '../../shared/types/simulator.types';
import type { SimulationSummary } from '../../shared/hooks/useSimulatorWizard';
import type { MobileKioskConfigValues } from '../components/MobileKioskConfigForm';
import {
  TerminalType,
  MOBILE_KIOSK_CHANNEL,
  PHYSICAL_KIOSK_CHANNEL,
} from '../types';
import {
  JourneyEventCode,
  SectionCode,
  GuestType,
  KeyDeliveryMethod,
  createCorrelatedEvent,
  type JourneyState,
} from '../../shared/types/journey.types';
import {
  generateAlternateReservationNumbers,
  generateRoomNumber,
  generatePaymentData,
  generateSectionDuration,
  createJourneyState,
  generateGuestName,
} from '../../shared/utils/journeyDataGenerator';

type LogFn = (type: LogEntry['type'], message: string, details?: any) => void;

const ERROR_RATE = 0.05;
const ABANDONMENT_RATE = 0.03;

/**
 * Helper to simulate a section (enter + wait + exit)
 */
const simulateSection = async (
  client: AnalyticsClient,
  addLog: LogFn,
  deviceId: string,
  hotelId: string,
  journey: JourneyState,
  channel: string,
  section: SectionCode,
  sectionLabel: string,
  action: string,
  terminalType: TerminalType,
  extraExitMetadata?: Record<string, string>,
): Promise<number> => {
  let eventsCount = 0;
  const duration = generateSectionDuration(section);
  addLog('info', `[${terminalType}] Section ${section} - ${sectionLabel} (${duration}s)`);

  const enterEvent = createCorrelatedEvent(
    JourneyEventCode.SECTION_ENTER,
    deviceId,
    hotelId,
    journey,
    {
      sectionCode: section,
      action,
      terminalType,
    }
  );
  enterEvent.channel = channel;
  await client.sendEvent(enterEvent);
  journey.currentSection = section;
  await new Promise(resolve => setTimeout(resolve, duration * 100));

  const exitEvent = createCorrelatedEvent(
    JourneyEventCode.SECTION_EXIT,
    deviceId,
    hotelId,
    journey,
    {
      sectionCode: section,
      duration: duration.toString(),
      terminalType,
      ...extraExitMetadata,
    }
  );
  exitEvent.channel = channel;
  await client.sendEvent(exitEvent);
  journey.completedSections.push(section);
  journey.currentSection = undefined;
  eventsCount += 2;

  return eventsCount;
};

export const useMobileKioskSimulation = () => {
  const simulate = useCallback(async (
    client: AnalyticsClient,
    addLog: LogFn,
    deviceId: string,
    hotelId: string,
    config: MobileKioskConfigValues,
  ): Promise<SimulationSummary> => {
    const startTime = Date.now();
    let totalEvents = 0;
    let successful = 0;
    let failed = 0;
    let abandoned = 0;

    const isMobile = config.terminalType === TerminalType.MOBILE;
    const channel = isMobile ? MOBILE_KIOSK_CHANNEL : PHYSICAL_KIOSK_CHANNEL;

    // Determine reservation number to use
    const reservationToUse = (!config.autoGenerateReservation && config.reservationNumber)
      ? config.reservationNumber
      : undefined;

    // Create journey state
    const journey = createJourneyState({
      guestType: config.guestType,
      hasPreCheckIn: false,
      reservationNumber: reservationToUse,
    });

    // Add multiple reservation numbers if needed
    if (Math.random() > 0.7) {
      journey.alternateReservationNumbers = generateAlternateReservationNumbers(journey.reservationNumber!);
      addLog('info', `Multiple reservations: ${journey.reservationNumber} + ${journey.alternateReservationNumbers.join(', ')}`);
    }

    // Assign room number
    journey.roomNumber = generateRoomNumber();

    addLog('info', '========================================');
    addLog('info', `Starting ${config.guestType === GuestType.WALKING ? 'WALKING' : 'RESERVATION'} check-in`);
    addLog('info', `Terminal: ${config.terminalType} (Channel: ${channel})`);
    addLog('info', `Reservation: ${journey.reservationNumber}`);
    if (journey.alternateReservationNumbers && journey.alternateReservationNumbers.length > 0) {
      addLog('info', `Alternate: ${journey.alternateReservationNumbers.join(', ')}`);
    }
    addLog('info', '========================================');

    try {
      // --- Abandonment check ---
      if (Math.random() < ABANDONMENT_RATE) {
        const abandonSections = [SectionCode.S, SectionCode.I, SectionCode.F, SectionCode.U, SectionCode.P];
        const abandonedAt = abandonSections[Math.floor(Math.random() * abandonSections.length)];

        addLog('warning', `[${config.terminalType}] Guest abandoned at section ${abandonedAt}`);

        const abEvent = createCorrelatedEvent(
          JourneyEventCode.AB,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: abandonedAt,
            abandonReason: 'user_exit',
            duration: Math.floor((Date.now() - startTime) / 1000).toString(),
            terminalType: config.terminalType,
          }
        );
        abEvent.channel = channel;
        await client.sendEvent(abEvent);
        totalEvents++;
        abandoned++;

        return {
          success: false,
          totalEvents,
          successful,
          failed,
          abandoned,
          duration: Date.now() - startTime,
          error: 'Abandoned',
        };
      }

      // --- CI_START ---
      addLog('info', `[${config.terminalType}] Guest arrives`);

      const ciStartEvent = createCorrelatedEvent(
        JourneyEventCode.CI_START,
        deviceId,
        hotelId,
        journey,
        {
          guestType: config.guestType,
          terminalType: config.terminalType,
        }
      );
      ciStartEvent.channel = channel;
      await client.sendEvent(ciStartEvent);
      totalEvents++;
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- Section S - SEARCH ---
      totalEvents += await simulateSection(
        client, addLog, deviceId, hotelId, journey, channel,
        SectionCode.S, 'Search', 'searchReservation', config.terminalType,
      );
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- Section I - IDENTIFICATION ---
      totalEvents += await simulateSection(
        client, addLog, deviceId, hotelId, journey, channel,
        SectionCode.I, 'Identification', 'guestIdentification', config.terminalType,
        { idType: 'passport' },
      );
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- Section F - SIGNATURE ---
      totalEvents += await simulateSection(
        client, addLog, deviceId, hotelId, journey, channel,
        SectionCode.F, 'Signature', 'digitalSignature', config.terminalType,
        { signatureType: 'digital' },
      );
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- Section U - UPSELLING ---
      const acceptedUpsell = Math.random() > 0.5;
      totalEvents += await simulateSection(
        client, addLog, deviceId, hotelId, journey, channel,
        SectionCode.U, 'Upselling', 'upsellOffers', config.terminalType,
        { offersShown: '3', offersAccepted: acceptedUpsell ? '1' : '0' },
      );
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- Section P - PAYMENT ---
      const paymentDuration = generateSectionDuration(SectionCode.P);
      const paymentFailed = Math.random() < ERROR_RATE;
      const payment = generatePaymentData();

      addLog('info', `[${config.terminalType}] Section P - Payment (${paymentDuration}s)`);

      const pmEnterEvent = createCorrelatedEvent(
        JourneyEventCode.SECTION_ENTER,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          action: 'processPayment',
          terminalType: config.terminalType,
        }
      );
      pmEnterEvent.channel = channel;
      await client.sendEvent(pmEnterEvent);
      journey.currentSection = SectionCode.P;
      totalEvents++;
      await new Promise(resolve => setTimeout(resolve, paymentDuration * 100));

      if (paymentFailed) {
        addLog('error', `[${config.terminalType}] Payment FAILED (EUR${payment.amount})`);

        const pmErrorEvent = createCorrelatedEvent(
          JourneyEventCode.PM_ERROR,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: SectionCode.P,
            errorCode: 'CARD_DECLINED',
            amount: payment.amount.toString(),
            paymentMethod: payment.method,
            terminalType: config.terminalType,
          }
        );
        pmErrorEvent.channel = channel;
        await client.sendEvent(pmErrorEvent);
        totalEvents++;
        failed++;

        return {
          success: false,
          totalEvents,
          successful,
          failed,
          abandoned,
          duration: Date.now() - startTime,
          error: 'Payment failed',
        };
      }

      // Payment successful
      addLog('success', `[${config.terminalType}] Payment SUCCESSFUL (EUR${payment.amount})`);

      const pmSuccessEvent = createCorrelatedEvent(
        JourneyEventCode.PM_SUCCESS,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          amount: payment.amount.toString(),
          paymentMethod: payment.method,
          operationId: payment.operationId,
          terminalType: config.terminalType,
        }
      );
      pmSuccessEvent.channel = channel;
      await client.sendEvent(pmSuccessEvent);
      totalEvents++;

      const pmExitEvent = createCorrelatedEvent(
        JourneyEventCode.SECTION_EXIT,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          duration: paymentDuration.toString(),
          terminalType: config.terminalType,
        }
      );
      pmExitEvent.channel = channel;
      await client.sendEvent(pmExitEvent);
      journey.completedSections.push(SectionCode.P);
      journey.currentSection = undefined;
      totalEvents++;
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- CI_COMP ---
      addLog('success', `[${config.terminalType}] CHECK-IN COMPLETED`);

      const ciCompEvent = createCorrelatedEvent(
        JourneyEventCode.CI_COMP,
        deviceId,
        hotelId,
        journey,
        {
          pmsRegistered: 'true',
          totalDuration: Math.floor((Date.now() - startTime) / 1000).toString(),
          terminalType: config.terminalType,
          keyDeliveryMethod: config.keyDeliveryMethod,
        }
      );
      ciCompEvent.roomNumber = journey.roomNumber;
      // Full name, top-level like a real kiosk: Insight reduces it to «Juan G.».
      const guest = generateGuestName();
      ciCompEvent.guestName = guest.full;
      ciCompEvent.channel = channel;
      await client.sendEvent(ciCompEvent);
      journey.checkInCompleted = true;
      totalEvents++;

      await client.updateJourney({
        reservationNumber: journey.reservationNumber!,
        alternateReservationNumbers: journey.alternateReservationNumbers,
        hotelId,
        channel,
        eventType: 'CheckInCompleted',
        timestamp: new Date().toISOString(),
      });

      // --- sendCheckIn ---
      await client.sendCheckIn({
        reservationNumber: journey.reservationNumber!,
        alternateReservationNumbers: journey.alternateReservationNumbers,
        guestNameMasked: guest.full,
        documentMasked: `DOC_${journey.reservationNumber!.slice(-4)}`,
        roomNumber: journey.roomNumber!,
        amountCharged: payment.amount,
        paymentMethod: payment.method,
        paymentOperationId: payment.operationId,
        deviceId,
        hotelId,
        channel,
        type: config.guestType === GuestType.WALKING ? 'Walking' : 'Reservation',
      });
      addLog('success', `[${config.terminalType}] Guest registered - Room ${journey.roomNumber}`);
      await new Promise(resolve => setTimeout(resolve, 300));

      // --- Key delivery ---
      await new Promise(resolve => setTimeout(resolve, 300));

      // Key delivery error check
      if (Math.random() < ERROR_RATE) {
        addLog('error', '[Key Delivery] Error delivering key');

        const kdErrorEvent = createCorrelatedEvent(
          JourneyEventCode.KD_ERROR,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: SectionCode.K,
            errorCode: 'KEY_DELIVERY_FAILED',
            terminalType: config.terminalType,
            deliveryMethod: config.keyDeliveryMethod,
          }
        );
        kdErrorEvent.roomNumber = journey.roomNumber;
        kdErrorEvent.channel = channel;
        await client.sendEvent(kdErrorEvent);
        totalEvents++;
        failed++;

        // Check-in completed but key failed - still partial success
        return {
          success: true,
          totalEvents,
          successful: successful + 1,
          failed,
          abandoned,
          duration: Date.now() - startTime,
        };
      }

      // Key delivery success
      const method = config.keyDeliveryMethod;
      if (method === KeyDeliveryMethod.QR_DISPENSER) {
        addLog('success', `[${config.terminalType}] QR code generated for dispenser pickup`);
      } else if (method === KeyDeliveryMethod.DIGITAL_PIN || method === KeyDeliveryMethod.DIGITAL_MOBILE) {
        addLog('success', `[${config.terminalType}] Digital key delivered via ${method}`);
      } else if (method === KeyDeliveryMethod.MAGNETIC_CARD || method === KeyDeliveryMethod.RFID_CARD || method === KeyDeliveryMethod.CONVENTIONAL_KEY) {
        addLog('success', `[${config.terminalType}] Physical key dispensed: ${method}`);
      } else {
        addLog('info', `[${config.terminalType}] Guest instructed to pick up key at reception`);
      }

      const kdSuccessEvent = createCorrelatedEvent(
        JourneyEventCode.KD_SUCCESS,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.K,
          terminalType: config.terminalType,
          deliveryMethod: config.keyDeliveryMethod,
        }
      );
      kdSuccessEvent.roomNumber = journey.roomNumber;
      kdSuccessEvent.channel = channel;
      await client.sendEvent(kdSuccessEvent);
      totalEvents++;
      successful++;

      await client.updateJourney({
        reservationNumber: journey.reservationNumber!,
        alternateReservationNumbers: journey.alternateReservationNumbers,
        hotelId,
        channel,
        eventType: 'KeyDispensed',
        timestamp: new Date().toISOString(),
      });

      journey.keyDispensed = true;

      // Final summary log
      const duration = Date.now() - startTime;
      addLog('success', '========================================');
      addLog('success', `Check-in COMPLETE (${(duration / 1000).toFixed(1)}s)`);
      addLog('success', `Room: ${journey.roomNumber}`);
      addLog('success', `Key delivered via ${config.keyDeliveryMethod}`);
      addLog('success', '========================================');

      return {
        success: true,
        totalEvents,
        successful,
        failed,
        abandoned,
        duration,
      };
    } catch (error: any) {
      addLog('error', `Check-in failed: ${error.message}`);

      return {
        success: false,
        totalEvents,
        successful,
        failed: failed + 1,
        abandoned,
        duration: Date.now() - startTime,
        error: error.message,
      };
    }
  }, []);

  return { simulate };
};
